import {
  ChangeEvent,
  FC,
  ReactNode,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import type { DropdownProps } from 'antd'
import cn from 'classnames'
import CascadingDropdown from '@UI/New/CascadingDropdown'
import { InputNew } from '@UI/InputNew'
import Preloader from '@UI/Preloader'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { useCrmLeadStickerTreeSearch } from '../../lib/hooks/useCrmLeadStickerTreeSearch'
import { useCrmMultipleDealsEnabled } from '../../lib/hooks/useCrmMultipleDealsEnabled'
import {
  buildCrmLeadCascadingMenuItems,
  type CrmLeadStickerPickPayload,
} from '../../lib/taskTrackerCrmLeadSticker.menu.utils'
import classes from './index.module.css'

const ROOT_LIST_MAX_HEIGHT = '16rem'
const SCROLL_LOAD_MORE_THRESHOLD_PX = 48

export type CrmLeadStickerCascadingPickerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  disabled?: boolean
  placement?: DropdownProps['placement']
  overlayClassName?: string
  rootDataAttr?: string
  onPick: (payload: CrmLeadStickerPickPayload) => void
}

const CrmLeadStickerCascadingPicker: FC<CrmLeadStickerCascadingPickerProps> = ({
  open,
  onOpenChange,
  children,
  disabled = false,
  placement = 'bottomLeft',
  overlayClassName,
  rootDataAttr = 'data-task-tracker-crm-lead-sticker',
  onPick,
}) => {
  const loadMoreLockRef = useRef(false)
  const { multipleDealsEnabled } = useCrmMultipleDealsEnabled()

  const {
    searchInput,
    setSearchInput,
    items,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMore,
    reset,
  } = useCrmLeadStickerTreeSearch({ enabled: open })

  useEffect(() => {
    if (!open) {
      reset()
      loadMoreLockRef.current = false
    }
  }, [open, reset])

  useEffect(() => {
    if (!loadingMore) {
      loadMoreLockRef.current = false
    }
  }, [loadingMore])

  const menuItems = useMemo(
    () =>
      buildCrmLeadCascadingMenuItems({
        leads: items,
        onPick,
        multipleDealsEnabled,
      }),
    [items, multipleDealsEnabled, onPick],
  )

  const panelTitles = useMemo(
    () =>
      multipleDealsEnabled
        ? ['Выберите лида', 'Воронка', 'Сделка']
        : ['Выберите лида', 'Воронка'],
    [multipleDealsEnabled],
  )

  const handleRootScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      if (!hasMore || loading || loadingMore || loadMoreLockRef.current) return

      const target = event.currentTarget
      const distanceToBottom =
        target.scrollHeight - (target.scrollTop + target.clientHeight)

      if (distanceToBottom <= SCROLL_LOAD_MORE_THRESHOLD_PX) {
        loadMoreLockRef.current = true
        void loadMore()
      }
    },
    [hasMore, loadMore, loading, loadingMore],
  )

  const rootEmptyContent = useMemo(() => {
    if (loading) {
      return (
        <span className={classes.emptyRow}>
          <Preloader />
        </span>
      )
    }
    if (error) {
      return <span className={classes.emptyRow}>{error}</span>
    }
    return <span className={classes.emptyRow}>Ничего не найдено</span>
  }, [error, loading])

  const rootHeader = (
    <div className={classes.searchWrap}>
      {/* @ts-ignore */}
      <InputNew
        className={cn(
          iclasses.newInputDesign,
          iclasses.newInputCompact,
          classes.searchInput,
        )}
        placeholder="Поиск по списку"
        value={searchInput}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSearchInput(event.currentTarget.value)
        }}
      />
      {error && items.length > 0 ? (
        <span className={classes.emptyRow}>{error}</span>
      ) : null}
    </div>
  )

  const rootListFooter = loadingMore ? (
    <span className={classes.loadingMore}>
      <Preloader />
    </span>
  ) : null

  return (
    <CascadingDropdown
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled}
      placement={placement}
      trigger={[]}
      destroyPopupOnHide
      overlayClassName={cn(classes.dropdown, overlayClassName)}
      rootDataAttr={rootDataAttr}
      panelTitles={panelTitles}
      items={menuItems}
      rootHeader={rootHeader}
      rootListFooter={rootListFooter}
      onRootScroll={handleRootScroll}
      rootListMaxHeight={ROOT_LIST_MAX_HEIGHT}
      rootEmptyContent={rootEmptyContent}
    >
      {children}
    </CascadingDropdown>
  )
}

export default CrmLeadStickerCascadingPicker
